import axios, { AxiosError } from 'axios';

// Utils
import {
  cleanUserTokensFromLocalStorage,
  getAccessToken,
  getRefreshToken,
  getSessionToken,
  setUserTokensToLocalStorage,
} from '../../utils/user';

// Constants
import URL from '../../constants/api-constants';
import ROUTE from '../../constants/route-constants';
import { UNKNOWN_ERROR } from '../../constants/common';
import { cleanOrganizationFromLocalStorage, getOrganization } from '../../utils/organization';

export const baseURL = import.meta.env.VITE_APP_API_URL;

const http = axios.create({
  baseURL,
  headers: {
    'Cache-Control': 'no-cache, no-store',
    Pragma: 'no-cache',
    Expires: '0',
  },
});

const getErrorMessage = (error: unknown) => {
  if (error instanceof AxiosError) {
    return error.response?.data?.message ?? UNKNOWN_ERROR;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return UNKNOWN_ERROR;
};

const isUnauthorizedRefreshFailure = (error: unknown) => {
  if (error instanceof AxiosError) {
    const message = error.response?.data?.message;
    const status = error.response?.status;

    return status === 401 || (status === 400 && message === 'Unauthorized');
  }

  return error instanceof Error && error.message === 'Unauthorized';
};

const clearAuthAndRedirect = () => {
  cleanUserTokensFromLocalStorage();
  cleanOrganizationFromLocalStorage();

  if (window.location.pathname !== ROUTE.AUTH_SIGN_IN) {
    window.location.assign(ROUTE.AUTH_SIGN_IN);
  }
};

http.interceptors.request.use(
  async (config) => {
    const accessToken = getAccessToken();
    const sessionToken = getSessionToken();
    const currentOrganizationId = getOrganization();

    if (accessToken) {
      config.headers?.set('Authorization', `Bearer ${accessToken}`);
      config.headers?.set('Session', `Bearer ${sessionToken}`);
      config.headers?.set('OrganizationId', currentOrganizationId);
    }

    return config;
  },
  // (error) => Promise.reject(error),
);

http.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    if (axios.isCancel(error)) {
      throw error;
    }

    const errorMessage = getErrorMessage(error);

    if (error?.response?.status === 401) {
      try {
        const refreshToken = getRefreshToken();
        const currentOrganizationId = getOrganization();

        if (!refreshToken && window.location.pathname !== ROUTE.AUTH_SIGN_IN) {
          clearAuthAndRedirect();
          throw new Error(errorMessage);
        }

        if (!refreshToken && window.location.pathname === ROUTE.AUTH_SIGN_IN) {
          throw new Error(errorMessage);
        }

        const response = await axios.post(`${baseURL}${URL.REFRESH_TOKEN_URL}`, {
          refreshToken,
        });

        const { access_token, session_token } = response.data;

        const refresh_token = refreshToken as string;
        setUserTokensToLocalStorage(access_token, refresh_token, session_token);
        error.config.headers = {
          ...error.config.headers,
          Authorization: `Bearer ${access_token}`,
          Session: `Bearer ${session_token}`,
          OrganizationId: currentOrganizationId,
        };

        const data = await http.request(error.config);
        return data;
      } catch (error) {
        if (isUnauthorizedRefreshFailure(error)) {
          clearAuthAndRedirect();
          throw new Error(getErrorMessage(error));
        }

        if (error instanceof AxiosError) {
          throw new Error(getErrorMessage(error));
        }

        throw error;
      }
    }

    throw error;
  },
);

export default http;
